import * as LocalStorage from '../../utility/local.storage';
import { DebugManager } from '../../utility/DebugManager';

// ─── Types ──────────────────────────────────────────

/** Caller-supplied validator. Payloads that fail it never reach the listener. */
export type MessageGuard<T> = (value: unknown) => value is T;

// ─── Diagnostics ────────────────────────────────────

/** Standard diagnostics for transport failures, excluding channel payloads. */
export const browserChannelDebug = new DebugManager({
  namespace: 'BrowserChannel'
});

// ─── Helpers ────────────────────────────────────────

/** The single localStorage key this channel echoes through. */
export function browserChannelStorageKey(channelName: string): string {
  return `${channelName}:message`;
}

// ─── Class ──────────────────────────────────────────

/**
 * Broadcasts messages to other tabs of this application on the same origin.
 *
 * Delivery takes two paths. `BroadcastChannel` reaches tabs that are already
 * listening, and the same payload is written to one localStorage key so a tab
 * that was not listening yet still gets a `storage` event. Both paths feed the
 * same subscribers, so a listener can be called twice for one publish and must
 * tolerate that.
 *
 * This class knows nothing about sessions, logout, or idle. Another feature
 * that needs its own cross-tab traffic constructs its own instance under a
 * different name rather than adding message types here.
 */
export class BrowserChannel {
  private channel?: BroadcastChannel;

  private listening = false;

  private readonly listeners = new Set<(message: unknown) => void>();

  constructor(
    private readonly channelName: string,
    private readonly debugManager: Pick<
      DebugManager,
      'log'
    > = browserChannelDebug
  ) {}

  /** Sends to the other tabs, and to this tab's own subscribers. */
  publish(message: object): void {
    this.connect();

    try {
      this.channel?.postMessage(message);
    } catch {
      this.logFailure(
        'Broadcast delivery failed; storage fallback will continue'
      );
      // The localStorage echo still has to go out.
    }

    try {
      LocalStorage.write(browserChannelStorageKey(this.channelName), message);
    } catch {
      this.logFailure('Storage echo failed; local delivery will continue');
      // Local subscribers still have to be told.
    }

    this.receive(message);
  }

  /**
   * Registers a listener for payloads that satisfy `guard`.
   *
   * @returns a function that removes this listener.
   */
  subscribe<T>(
    guard: MessageGuard<T>,
    listener: (message: T) => void
  ): () => void {
    this.connect();

    const validatedListener = (message: unknown): void => {
      if (!guard(message)) return;

      try {
        listener(message);
      } catch {
        this.logFailure(
          'Subscriber failed; remaining subscribers will continue'
        );
        // One failing subscriber must not block the others.
      }
    };

    this.listeners.add(validatedListener);

    return () => {
      this.listeners.delete(validatedListener);
    };
  }

  /** Releases the browser channel and the storage listener. */
  dispose(): void {
    this.channel?.close();
    this.channel = undefined;

    window.removeEventListener('storage', this.onStorage);

    this.listening = false;
    this.listeners.clear();
  }

  /** Opens browser resources on first use, so construction stays inert. */
  private connect(): void {
    if (this.listening) return;

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel(this.channelName);
        this.channel.onmessage = (event: MessageEvent<unknown>) => {
          this.receive(event.data);
        };
      }
    } catch {
      this.logFailure(
        'Broadcast channel could not be opened; storage delivery will continue'
      );
      // Storage delivery covers us when the channel cannot be constructed.
    }

    // Always listen for storage too: the live message may never arrive.
    window.addEventListener('storage', this.onStorage);
    this.listening = true;
  }

  private receive(message: unknown): void {
    this.listeners.forEach((listener) => listener(message));
  }

  /**
   * Keeps diagnostics useful without retaining a feature's channel payload or
   * a subscriber error that may contain feature data.
   */
  private logFailure(message: string): void {
    this.debugManager.log('state', message, {
      channelName: this.channelName
    });
  }

  /** Storage events fire in the *other* tabs, never in the one that wrote. */
  private readonly onStorage = (event: StorageEvent): void => {
    if (
      event.storageArea !== window.localStorage ||
      event.key !== browserChannelStorageKey(this.channelName) ||
      event.newValue === null
    )
      return;

    try {
      this.receive(JSON.parse(event.newValue));
    } catch {
      this.logFailure('Malformed storage message was ignored');
      // A payload that is not JSON cannot be a message of ours.
    }
  };
}
